import { ReadonlySignal, computed, effect, signal } from "@preact/signals";
import AppHistory from "~/core/AppHistory";
import router from "~/core/router";
import { me, store } from "~/core/store";
import { DiscoveryAction } from "~/organisms/DiscoveryItem";
import { makeAdmin } from "./admin";
import { HelpHistoryRecord, MakeHelpParams } from "./interfaces";
import { HelpTag, TAGS } from "./tags";
import { makeTree } from "./utils";

const STATE_KEY = "jh";
const RENDER_WAIT = 50;

export const SIDEBAR_TAGS: HelpTag[] = [
    "admin-time.month.add-activity",
    "admin-time.month.contracts",
    "admin-time.month.day-status-selector",
    "admin-time.shared.report-all",
];

// @TODO: revisit action (goto, open_discovery, admin_time_open_sidebar) processing

const makeHelp = (params: MakeHelpParams) => {
    const isDiscovery = signal(false);
    const tags = computed(() => TAGS);
    const tagsTree = computed(() =>
        makeTree(tags.value.filter((i) => i.tag.at(-1) !== "?")),
    );
    const demoDay = signal<Date | null>(null);
    const weekDemoDay = signal<Date | null>(null);
    const history = signal<HelpHistoryRecord[]>([]);
    const oldHistory = signal<HelpHistoryRecord[]>([]);
    const activeHistoryIndex = signal(0);
    const activeHistoryRecord = computed<HelpHistoryRecord | null>(
        () => history.value[activeHistoryIndex.value] ?? null,
    );
    const tagOpenState: Record<string, boolean> = {};
    let openDiscoveryId = 0;

    effect(() => {
        const d = isDiscovery.value;

        if (!d && demoDay.peek()) {
            demoDay.value = null;
        }

        if (!d && weekDemoDay.peek()) {
            weekDemoDay.value = null;
        }
    });

    window.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
            closeHistory();
        }
    });

    AppHistory.on((e) => {
        // @DEP react-router provides user state in usr key
        //
        const state = e.state?.usr ?? e.state;

        if (state && state[STATE_KEY] !== undefined) {
            const v = state[STATE_KEY];

            if (typeof v === "number") {
                if (
                    history.peek().length === 0 &&
                    oldHistory.peek().length > 0
                ) {
                    history.value = oldHistory.value.slice();
                    oldHistory.value = [];
                }

                navigateHistoryTo(v, false);
            }
        } else {
            closeHistory(false);
        }
    });

    const adjustHistory = (
        action: DiscoveryAction,
        sourceTag: HelpTag,
        sourcePath: string,
        sourceTitle: string,
        index: number,
        actions: DiscoveryAction[],
        ctx?: Record<string, unknown> | undefined,
    ) => {
        const detail = Object.assign({}, action.params ?? {}, ctx);
        const prevGoto = actions.find((a, i) => i < index && a.key === "goto");
        const curIdx = activeHistoryIndex.peek();
        const ctxId = ctx?.$id;

        const prevSidebar = actions.find(
            (a, i) => i < index && a.key === "admin_time_open_sidebar",
        );

        if (prevSidebar) {
            ctx = { ...ctx, $sidebar: true };
        }

        if (ctx) {
            delete ctx.$id;
        }

        const newHistory = [
            ...history.peek().slice(0, curIdx + 1),
            {
                path: `${window.location.pathname}${window.location.search}`,
                tag: detail.tag as HelpTag,
                title: store.meta.title.peek(),
                ctx,
            },
        ];

        if (history.peek().length === 0) {
            newHistory.unshift({
                path: sourcePath,
                tag: sourceTag,
                title: sourceTitle,
                ctx: { $sidebar: SIDEBAR_TAGS.includes(sourceTag), id: ctxId },
            });
        }

        const penultimate = newHistory[newHistory.length - 2];

        if (penultimate && (typeof ctx?.scrollTop === "number" || ctxId)) {
            penultimate.ctx = {
                ...penultimate.ctx,
                scrollTop: ctx?.scrollTop,
                id: ctxId,
            };
        }

        if (history.peek().length === 0 && !prevGoto) {
            window.history.pushState(
                {
                    [STATE_KEY]: 0,
                    sidebar: SIDEBAR_TAGS.includes(sourceTag),
                },
                "",
                window.location.href,
            );
        }

        if (!prevGoto) {
            window.history.pushState(
                {
                    [STATE_KEY]: newHistory.length - 1,
                    sidebar: SIDEBAR_TAGS.includes(detail.tag as HelpTag),
                },
                "",
                window.location.href,
            );
        }

        history.value = newHistory;
        activeHistoryIndex.value = newHistory.length - 1;
    };

    const openDiscovery = async (
        tag: string,
        params?: Record<string, unknown>,
    ) => {
        const myId = ++openDiscoveryId;
        const detail = { tag, ...params };

        const promise = new Promise<boolean>((resolve) => {
            const handler = (e: Event) => {
                window.removeEventListener("discovery.item_opened", handler);
                resolve(e instanceof CustomEvent && e.detail.tag === tag);
            };

            window.setTimeout(() => {
                window.removeEventListener("discovery.item_opened", handler);
                resolve(false);
            }, 200);
            window.addEventListener("discovery.item_opened", handler);
        });

        window.dispatchEvent(
            new CustomEvent("discovery.open_discovery", { detail }),
        );

        const result = await promise;

        if (myId !== openDiscoveryId) {
            return;
        }

        if (result && tag.at(-1) === "?") {
            // adjust history
            //
            const curIdx = activeHistoryIndex.peek();
            const newHistory = history.peek().slice();

            newHistory[curIdx].tag = tag as HelpTag;

            history.value = newHistory;
        }

        if (!result && tag.at(-1) !== "?") {
            // if no tag opened, open fallback tag
            //
            openDiscovery(`${tag}?`);
        } else if (!result && params?.force !== true) {
            // if fallback tag can't be opened, try force-open it
            //
            openDiscovery(tag, { force: true });
        }
    };

    const processActionItem = (
        action: DiscoveryAction,
        sourceTag: HelpTag,
        sourcePath: string,
        sourceTitle: string,
        index: number,
        actions: DiscoveryAction[],
        ctx?: Record<string, unknown> | undefined,
    ) => {
        const detail = Object.assign({}, action.params ?? {}, ctx);

        if (action.key === "open_discovery" && detail?.tag) {
            adjustHistory(
                action,
                sourceTag,
                sourcePath,
                sourceTitle,
                index,
                actions,
                ctx,
            );
            openDiscovery(detail.tag, detail);
        } else if (action.key === "goto" && detail?.page) {
            const nextOpen = actions.find(
                (a, i) => i > index && a.key === "open_discovery",
            );
            const nextSidebar = actions.find(
                (a, i) => i > index && a.key === "admin_time_open_sidebar",
            );

            // @TODO: when in journey mode and single goto action without open_discovery
            // it breaks; decide what to do - either close history or open page's root tag

            if (nextOpen && history.peek().length === 0) {
                window.history.pushState(
                    {
                        [STATE_KEY]: 0,
                        sidebar: store.dialog.isMonthSidebarReady.peek(),
                    },
                    "",
                    window.location.href,
                );
            }

            const state: Record<string, unknown> = {};

            if (nextOpen) {
                state[STATE_KEY] = activeHistoryIndex.peek() + 1;
            }

            if (nextSidebar) {
                state.sidebar = true;
            }

            return router
                .navigate(me(detail.page), { state })
                .then(
                    () =>
                        new Promise((resolve) =>
                            setTimeout(resolve, RENDER_WAIT),
                        ),
                ); // wait for page to render
        } else if (action.key === "admin_time_open_sidebar") {
            return store.dialog.waitMonthSidebar({
                date: new Date(detail.date ?? new Date()),
            });
        } else {
            window.dispatchEvent(
                new CustomEvent(`discovery.${action.key}`, { detail }),
            );
        }
    };

    const processActions = async (
        actions: DiscoveryAction[],
        sourceTag: HelpTag,
        ctx?: Record<string, unknown> | undefined,
    ) => {
        const sourcePath = `${window.location.pathname}${window.location.search}`;
        const sourceTitle = store.meta.title.peek();

        for (let i = 0; i < actions.length; ++i) {
            await processActionItem(
                actions[i],
                sourceTag,
                sourcePath,
                sourceTitle,
                i,
                actions,
                ctx,
            );
        }
    };

    const processAction = (
        action: DiscoveryAction,
        sourceTag: HelpTag,
        ctx?: Record<string, unknown> | undefined,
    ) =>
        processActionItem(
            action,
            sourceTag,
            `${window.location.pathname}${window.location.search}`,
            store.meta.title.peek(),
            0,
            [action],
            ctx,
        );

    const navigateHistoryTo = async (index: number, goHistory = true) => {
        const newIndex = Math.max(
            0,
            Math.min(history.peek().length - 1, index),
        );
        const oldRecord = history.peek()[activeHistoryIndex.peek()];
        const newRecord = history.peek()[newIndex];
        const delta = newIndex - activeHistoryIndex.peek();

        if (!oldRecord || !newRecord || !delta) {
            return;
        }

        if (oldRecord.path !== newRecord.path) {
            cleanTagOpenState();
        }

        const oldScrollTop =
            document.querySelector<HTMLElement>(
                `[data-discovery-container="${oldRecord.tag}"]`,
            )?.scrollTop ?? 0;

        if (goHistory) {
            if (oldRecord.path !== newRecord.path) {
                await router
                    .navigate(delta)
                    .then(
                        () =>
                            new Promise((resolve) =>
                                setTimeout(resolve, RENDER_WAIT),
                            ),
                    ); // wait for page to render
            } else {
                window.history.go(delta);
            }
        }

        oldRecord.ctx = { ...oldRecord.ctx, scrollTop: oldScrollTop };
        activeHistoryIndex.value = newIndex;

        if (newRecord.ctx?.$sidebar) {
            await processAction(
                { key: "admin_time_open_sidebar" },
                newRecord.tag,
                newRecord.ctx,
            );
        }

        openDiscovery(
            newRecord.tag,
            newRecord.ctx?.id ? { id: newRecord.ctx.id } : undefined,
        );
    };

    const navigateHistory = (shift: number) =>
        navigateHistoryTo(activeHistoryIndex.peek() + shift);

    const closeHistory = (goHistory = true) => {
        if (history.peek().length === 0) {
            return;
        }

        if (goHistory) {
            window.history.pushState({}, "", window.location.href);
        }

        oldHistory.value = history.peek();
        history.value = [];
        activeHistoryIndex.value = 0;
        window.dispatchEvent(new CustomEvent("discovery.close-history"));
    };

    const cleanTagOpenState = () => {
        for (const key in tagOpenState) {
            delete tagOpenState[key];
        }
    };

    const isTagDefaultOpen = (tag: HelpTag) => {
        const isActive = activeHistoryRecord.peek()?.tag === tag;

        if (!isActive) {
            return false;
        }

        if (tagOpenState[tag] === undefined) {
            tagOpenState[tag] = true;

            return true;
        }

        return false;
    };

    const admin = makeAdmin({ ...params, closeHistory });

    return {
        admin,
        tags,
        history: history as ReadonlySignal<HelpHistoryRecord[]>,
        activeHistoryIndex: activeHistoryIndex as ReadonlySignal<number>,
        activeHistoryRecord,
        tagsTree,
        isDiscovery,
        demoDay,
        weekDemoDay,
        processAction,
        processActions,
        navigateHistory,
        navigateHistoryTo,
        isTagDefaultOpen,
        closeHistory,
    };
};

export default makeHelp;
